import { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Divider,
  Grid,
  Stack,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import * as yup from 'yup';

import { FormRoot, RenderFormField, SideModal } from '@/components';
import { Button } from '@/components/TailwindComponents';

const schema = yup.object().shape({
  companyName: yup.string().required('Company name is required'),
  contactPerson: yup.string().required('Contact person is required'),
  primaryPhone: yup.string().required('Primary phone is required'),
  secondaryPhone: yup.string().required('Secondary phone is required'),
  email: yup
    .string()
    .required('Email is required')
    .email('Invalid email format'),
  license: yup.string().required('License is required'),
  address: yup.string().required('Address is required'),
  serviceArea: yup.string().required('Service area is required'),
  isActive: yup.boolean(),
});

export type TowingCompanyFormValues = yup.InferType<typeof schema>;

interface TowingCompanyFormPanelProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: TowingCompanyFormValues) => void;
  defaultValues?: TowingCompanyFormValues;
  mode?: 'add' | 'edit';
}

export const TowingCompanyFormPanel: FC<TowingCompanyFormPanelProps> = ({
  open,
  onClose,
  onSubmit,
  defaultValues,
  mode = 'add',
}) => {
  const theme = useTheme();

  const methods = useForm<TowingCompanyFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      companyName: '',
      contactPerson: '',
      primaryPhone: '',
      secondaryPhone: '',
      email: '',
      license: '',
      address: '',
      serviceArea: '',
      isActive: false,
    },
    mode: 'all',
  });

  const { reset, handleSubmit, register, watch } = methods;

  useEffect(() => {
    if (defaultValues) {
      reset({
        companyName: defaultValues.companyName ?? '',
        contactPerson: defaultValues.contactPerson ?? '',
        primaryPhone: defaultValues.primaryPhone ?? '',
        secondaryPhone: defaultValues.secondaryPhone ?? '',
        email: defaultValues.email ?? '',
        license: defaultValues.license ?? '',
        address: defaultValues.address ?? '',
        serviceArea: defaultValues.serviceArea ?? '',
        isActive: defaultValues.isActive ?? false,
      });
    }
  }, [defaultValues, reset]);

  const handleFormSubmit = (data: TowingCompanyFormValues) => {
    onSubmit(data);

    reset({
      companyName: '',
      contactPerson: '',
      primaryPhone: '',
      secondaryPhone: '',
      email: '',
      license: '',
      address: '',
      serviceArea: '',
      isActive: false,
    });
  };

  const handleClose = () => {
    reset({
      companyName: '',
      contactPerson: '',
      primaryPhone: '',
      secondaryPhone: '',
      email: '',
      license: '',
      address: '',
      serviceArea: '',
      isActive: false,
    });
    onClose();
  };

  return (
    <SideModal open={open} onClose={handleClose} width="600px">
      <Stack
        sx={{
          backgroundColor: theme.palette.background.default,
          height: '100%',
          padding: '20px',
          overflowY: 'auto',
        }}
      >
        <Typography
          variant="h2"
          sx={{ fontWeight: 600, fontSize: '1.5rem', mb: 2 }}
        >
          {mode === 'edit' ? 'Edit towing company' : 'Add towing company'}
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <FormProvider {...methods}>
          <FormRoot
            onSubmit={handleSubmit(handleFormSubmit)}
            sx={{ padding: 0 }}
            noValidate
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <RenderFormField
                  name="companyName"
                  label="Company name"
                  rules={{ required: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <RenderFormField
                  name="contactPerson"
                  label="Contact person"
                  rules={{ required: true }}
                />
              </Grid>

              <Grid item xs={6}>
                <RenderFormField
                  name="primaryPhone"
                  label="Primary phone"
                  type="pattern"
                  format="(###) ###-####"
                  rules={{
                    required: true,
                    pattern: {
                      value: /[2-9][0-9]{9}$/im,
                      message: 'Invalid phone number.',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <RenderFormField
                  name="secondaryPhone"
                  label="Secondary phone"
                  type="pattern"
                  format="(###) ###-####"
                  rules={{
                    required: true,
                    pattern: {
                      value: /[2-9][0-9]{9}$/im,
                      message: 'Invalid phone number.',
                    },
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <RenderFormField
                  name="email"
                  label="Email address"
                  rules={{ required: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <RenderFormField
                  name="license"
                  label="License number"
                  rules={{ required: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <RenderFormField
                  name="address"
                  label="Address"
                  rules={{ required: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <RenderFormField
                  name="serviceArea"
                  label="Service area"
                  multiline
                  rows={3}
                  placeholder="Geographic areas covered by this company..."
                  rules={{ required: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <Stack direction="row" alignItems="center" gap={2}>
                  <Switch
                    {...register('isActive')}
                    checked={watch('isActive')}
                  />
                  <Typography>Active in rotation</Typography>
                </Stack>
              </Grid>
            </Grid>

            <Box width="100%">
              <Stack direction="row" justifyContent="flex-end" gap={2} mt={4}>
                <Button variant="outline" type="button" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit">
                  {mode === 'edit' ? 'Update' : 'Save'}
                </Button>
              </Stack>
            </Box>
          </FormRoot>
        </FormProvider>
      </Stack>
    </SideModal>
  );
};
